import { BRAND_SITE_URL, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
/**
 * Membresía Premium VIP — página exclusiva que inicia directamente con el video VIP.
 *
 * Esta página ignora la desactivación del plan VIP y abre automáticamente el
 * reproductor de video Mux al cargar, permitiendo acceso directo al contenido VIP.
 *
 * Uso: /membresia-vip
 *
 * Características:
 * - Inicia automáticamente con el video VIP abierto (MuxVideoPlayerWithCTA)
 * - Ignora el flag VITE_VIP_PLAN_CARD_ENABLED
 * - Todo pago va por Stripe (MercadoPago deshabilitado para membresías).
 * - Detecta país e idioma automáticamente
 *
 * Precios → env vars (Railway): VITE_PREMIUM_USD_*  (la rama ARS quedó deshabilitada
 *           porque las membresías solo cobran USD por Stripe; el Stripe Price ID
 *           se administra en /admin/suscripciones → membership_products.plan_tier=vip).
 * Videos  → env vars (Railway): VITE_PREMIUM_MUX_*
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import {
  getPlansForCurrency,
  getLabelsForLanguage,
  type BioTemplatePremiumLabels,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumWebsitePaths,
  PREMIUM_WEBSITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSiteSettingsVariant } from "@/hooks/useSiteSettingsVariant";
import { useInternationalHomeVipCheckoutPricing } from "@/hooks/useInternationalHomeVipCheckoutPricing";
import { useUserId } from "@/hooks/useUserId";
import {
  trackVipVideoOpenedPostHog,
  trackVipVideoClosedPostHog,
  trackCheckoutPanelOpenedPostHog,
  identifyUserByEmailPostHog,
} from "@/lib/posthogAnalytics";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";
import { initConversionTracking } from "@/lib/conversionAnalytics";
import { getDatafastAttributionPayload } from "@/lib/datafast";

export default function MembresiaPremiumVip() {
  const { country, language } = useTemplateSelection();
  // Membresías cobran solo USD por Stripe; precio VIP desde admin (country-templates).
  const currency = "USD" as const;
  const siteSettingsVariant = useSiteSettingsVariant();
  const { settings: siteSettings } = useSiteSettings(siteSettingsVariant);
  const { pricing: adminVipPricing, priceDisplay } =
    useInternationalHomeVipCheckoutPricing({
      country,
      vipPricingByCountry: siteSettings.internationalHomeVipPricingByCountry,
      vipPricingDefault: siteSettings.internationalHomeVipPricingDefault,
      language,
    });
  const { vipPlan } = getPlansForCurrency(currency, language, {
    vipUsd: adminVipPricing.fullPriceUsd,
  });
  const { successPath, cancelPath } = getPremiumWebsitePaths(currency);
  const L: BioTemplatePremiumLabels = getLabelsForLanguage(language);
  const navigate = useNavigate();
  const userId = useUserId();
  
  // Estados
  const [showVipVideo, setShowVipVideo] = useState(true); // Inicia con video abierto
  const [showCustomCheckout, setShowCustomCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const checkoutOpenSourceRef = useRef<string>("auto_open");
  const checkoutPanelOpenTimeRef = useRef<number>(Date.now());

  // Forzar VIP habilitado; price = admin full list price
  const vipPlanForced = {
    ...vipPlan,
    price: adminVipPricing.fullPriceUsd,
    isDisabled: false,
  };

  if (import.meta.env.DEV) {
    console.log("[MembresiaPremiumVip] Initialized:", {
      country,
      currency,
      language,
      vipPlaybackId: vipPlanForced.playbackId,
      autoOpenVideo: true,
    });
  }

  // Inicializar tracking de conversiones al cargar la página
  useEffect(() => {
    initConversionTracking();
  }, []);

  // Auto-abrir video al montar
  useEffect(() => {
    if (vipPlanForced.playbackId) {
      trackVipVideoOpenedPostHog(PREMIUM_WEBSITE_FLOW_NAME, vipPlanForced.playbackId, country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async () => {
    if (!customerEmail || !customerEmail.includes("@")) {
      setCheckoutError(L.emailRequired);
      return;
    }

    setIsProcessing(true);
    setCheckoutError(null);

    try {
      identifyUserByEmailPostHog(customerEmail, {
        userId,
        flow: PREMIUM_WEBSITE_FLOW_NAME,
        tier: "vip",
      });

      // Admin VIP: Price + promo (same as DualCTA). amountCents = first charge display.
      const amountCents =
        adminVipPricing.amountCents > 0
          ? adminVipPricing.amountCents
          : Math.round(vipPlanForced.price * 100);
      const listCents =
        adminVipPricing.fullPriceCents > 0
          ? adminVipPricing.fullPriceCents
          : Math.round(vipPlanForced.price * 100);
      const successUrl = `${window.location.origin}${successPath}`;
      const cancelUrl = `${window.location.origin}${cancelPath ?? "/invitacion-premium"}`;
      const response = await fetch("/api/create-subscription-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ...getDatafastAttributionPayload(),
          amount: amountCents,
          currency: "usd",
          productName: vipPlanForced.productName,
          buyerEmail: customerEmail,
          country,
          successUrl,
          cancelUrl,
          intervalCount: 1,
          ...(adminVipPricing.stripePriceId
            ? { priceId: adminVipPricing.stripePriceId }
            : {}),
          ...(adminVipPricing.promotionCodeId
            ? { promotionCodeId: adminVipPricing.promotionCodeId }
            : {}),
          metadata: {
            source: BRAND_SOURCE_KEY,
            flow: PREMIUM_WEBSITE_FLOW_NAME,
            flow_trace_id: userId,
            plan_tier: "vip",
            product_type: "exclusive",
            product_name: vipPlanForced.productName,
            buyer_email: customerEmail,
            country,
            language,
            tier: "vip",
            price_source: adminVipPricing.priceSource,
            list_amount_cents: String(listCents),
            ...(adminVipPricing.stripePriceId
              ? { stripe_price_id: adminVipPricing.stripePriceId }
              : {}),
          },
        }),
      });

      const data = (await response.json()) as { url?: string; error?: string; code?: string };

      if (!response.ok || !data.url) {
        const detail =
          typeof data.error === "string" && data.error
            ? data.code
              ? `${data.error} (${data.code})`
              : data.error
            : `Failed to create checkout session (HTTP ${response.status})`;
        throw new Error(detail);
      }

      const { url } = data;
      window.location.href = url;
    } catch (error) {
      console.error("[MembresiaPremiumVip] Checkout error:", error);
      Sentry.captureException(error, {
        tags: {
          component: "MembresiaPremiumVip",
          action: "checkout",
          currency,
        },
      });
      const message =
        error instanceof Error && error.message
          ? error.message
          : L.errorInvalidDefault;
      setCheckoutError(message);
      setIsProcessing(false);
    }
  };

  // Si no hay video VIP configurado, redirigir a la página normal
  if (!vipPlanForced.playbackId) {
    console.warn("[MembresiaPremiumVip] No VIP video configured, redirecting to /invitacion-premium");
    navigate("/invitacion-premium");
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Video VIP (auto-abierto) */}
      {showVipVideo && vipPlanForced.playbackId && (
        <div className="fixed inset-0 z-50 bg-black w-full h-dvh min-h-dvh overflow-hidden">
          <MuxVideoPlayerWithCTA
            countryCode={country}
            language={language.split("-")[0] as "en" | "es" | "de" | "fr" | "it" | "pt" | "nl"}
            playbackId={vipPlanForced.playbackId}
            forceManualPlaybackId={true}
            ctaSubtitle={vipPlanForced.subtitle}
            ctaUrl={`${BRAND_SITE_URL}/checkout-vip`}
            ctaLabel={L.joinVip}
            hideUnmutePrompt={true}
            onCTAClick={async () => {
              setShowVipVideo(false);
              checkoutOpenSourceRef.current = "video_cta";
              checkoutPanelOpenTimeRef.current = Date.now();

              const price =
                adminVipPricing.amountCents > 0
                  ? adminVipPricing.amountCents / 100
                  : vipPlanForced.price;

              trackCheckoutPanelOpenedPostHog(
                "vip",
                "video_cta",
                price,
                currency,
                PREMIUM_WEBSITE_FLOW_NAME,
                country,
              );
              
              setCheckoutError(null);
              setShowCustomCheckout(true);
            }}
            onClose={() => {
              setShowVipVideo(false);
              trackVipVideoClosedPostHog(PREMIUM_WEBSITE_FLOW_NAME, vipPlanForced.playbackId!, country);
              // Redirigir a la página principal si cierra el video
              navigate("/invitacion-premium");
            }}
          />
        </div>
      )}

      {/* Panel de Checkout — Stripe only (MercadoPago deshabilitado para membresías) */}
      {showCustomCheckout && (
          // Stripe: Modal centrado con input de email
          <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="relative w-full max-w-md bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border-b border-white/5 px-6 py-5">
                <h2 className="text-xl font-semibold text-white text-center">
                  {L.joinVip}
                </h2>
                {vipPlanForced.subtitle && (
                  <p className="text-xs text-gray-400 text-center mt-1">
                    {vipPlanForced.subtitle}
                  </p>
                )}
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Precio — admin VIP (country-templates / default) */}
                <div className="text-center">
                  {priceDisplay.originalPriceLine ? (
                    <div className="text-sm text-gray-500 line-through mb-1">
                      {priceDisplay.originalPriceLine}
                    </div>
                  ) : null}
                  <div className="text-3xl font-bold text-white">
                    {priceDisplay.priceLine}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {L.monthlyAutorenew}
                  </p>
                </div>

                {/* Error message */}
                {checkoutError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400 text-center">
                    {checkoutError}
                  </div>
                )}

                {/* Email input */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder={L.emailPlaceholder}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-transparent transition-all"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    {L.emailReceiptNote}
                  </p>
                </div>

                {/* Checkout button */}
                <button
                  onClick={handleCheckout}
                  disabled={isProcessing || !customerEmail}
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {language.startsWith("es") ? "Procesando..." : "Processing..."}
                    </>
                  ) : (
                    L.continueToCardPayment
                  )}
                </button>

                {/* Cancel button */}
                <button
                  onClick={() => {
                    setShowCustomCheckout(false);
                    setShowVipVideo(true);
                  }}
                  disabled={isProcessing}
                  className="w-full text-gray-400 hover:text-white text-sm transition-colors disabled:opacity-50"
                >
                  {language.startsWith("es") ? "Volver al video" : "Back to video"}
                </button>
                
                {/* Secure payment note */}
                <p className="text-xs text-center text-gray-500 mt-2">
                  {L.secureEncryptedPayment}
                </p>
              </div>
            </div>
          </div>
      )}
    </div>
  );
}
